/**
 * Accès au contenu pédagogique.
 * Le contenu est servi en statique depuis /content (copié par tools/sync-content.js).
 * Cette abstraction permet plus tard de basculer vers le proxy-server ou la RTDB
 * (contenu mis à jour sans nouvelle version store) sans toucher aux écrans.
 */
import type { Catalog, Chapter, ChapterMeta, FullChapter, Subject, Year } from "../content/types";

const BASE = "/content";
let catalogPromise: Promise<Catalog> | null = null;
const chapterCache = new Map<string, Promise<FullChapter>>();

const SHOW_DRAFTS = process.env.NODE_ENV !== "production" || process.env.REACT_APP_SHOW_DRAFT_CONTENT === "1";

async function fetchJson<T>(path: string): Promise<T> {
  const res = await fetch(`${BASE}${path}`, { cache: "no-cache" });
  if (!res.ok) throw new Error(`Contenu introuvable : ${path} (${res.status})`);
  return (await res.json()) as T;
}

export function getCatalog(): Promise<Catalog> {
  if (!catalogPromise) {
    catalogPromise = fetchJson<Catalog>("/catalog.json").then((c) => ({
      ...c,
      chapters: c.chapters
        .filter((ch) => SHOW_DRAFTS || ch.status === "published")
        .sort((a, b) => a.order - b.order),
    }));
    catalogPromise.catch(() => {
      catalogPromise = null;
    });
  }
  return catalogPromise;
}

export async function getSubjects(): Promise<Subject[]> {
  return (await getCatalog()).subjects;
}

export async function getSubject(subjectId: string): Promise<Subject | undefined> {
  return (await getSubjects()).find((s) => s.id === subjectId);
}

export async function getChaptersForSubject(subjectId: string, year?: Year): Promise<ChapterMeta[]> {
  const cat = await getCatalog();
  return cat.chapters.filter((c) => c.subjectId === subjectId && (!year || c.years.includes(year)));
}

export async function getChapterMeta(chapterId: string): Promise<ChapterMeta | undefined> {
  return (await getCatalog()).chapters.find((c) => c.id === chapterId);
}

export function getChapter(chapterId: string): Promise<FullChapter> {
  let p = chapterCache.get(chapterId);
  if (!p) {
    p = Promise.all([getChapterMeta(chapterId), fetchJson<Chapter>(`/chapters/${chapterId}.json`)]).then(
      ([meta, chapter]) => {
        if (!meta) throw new Error(`Chapitre absent du catalogue : ${chapterId}`);
        return { ...chapter, ...meta } as FullChapter;
      }
    );
    p.catch(() => chapterCache.delete(chapterId));
    chapterCache.set(chapterId, p);
  }
  return p;
}

export const THEORY_LEVEL_KEYS = {
  quick: "theory.quick",
  standard: "theory.standard",
  detailed: "theory.detailed",
} as const;
